/**
 * SlidePresenter — Reusable component for LIM presentation mode.
 *
 * Takes an array of Slide objects and renders a fullscreen, one-slide-at-a-time
 * presentation with touch/keyboard navigation, progress bar, and slide counter.
 *
 * Used by ArgumentDetailView, TappaReaderView, and AuthorDetailView when
 * `data-display="lim"` is active on <html>.
 */

export interface Slide {
  sectionTitle: string;
  subtitle?: string;
  html: string;
}

export interface SlidePresenterOptions {
  onExit: () => void;
  backLabel?: string;
}

export class SlidePresenter {
  private readonly container: HTMLElement;
  private readonly slides: Slide[];
  private readonly options: SlidePresenterOptions;
  private current = 0;

  private slideEl!: HTMLElement;
  private counterEl!: HTMLElement;
  private progressEl!: HTMLElement;
  private sectionTitleEl!: HTMLElement;

  private touchStartX = 0;
  private touchStartY = 0;

  private readonly boundKeydown = this.onKeydown.bind(this);

  constructor(container: HTMLElement, slides: Slide[], options: SlidePresenterOptions) {
    this.container = container;
    this.slides = slides;
    this.options = options;
  }

  mount(): void {
    this.container.innerHTML = '';
    this.container.classList.add('slide-presenter');

    this.container.innerHTML = `
      <header class="sp-header">
        <div class="sp-header-left">
          <button type="button" class="sp-exit" aria-label="Esci dalla presentazione">&times;</button>
          <span class="sp-section-title"></span>
        </div>
        <div class="sp-header-right">
          <span class="sp-counter"></span>
          <button type="button" class="sp-nav sp-prev" aria-label="Slide precedente">‹</button>
          <button type="button" class="sp-nav sp-next" aria-label="Slide successiva">›</button>
        </div>
      </header>
      <div class="sp-slide-viewport">
        <div class="sp-slide"></div>
      </div>
      <div class="sp-progress-track">
        <div class="sp-progress-bar"></div>
      </div>
    `;

    this.slideEl = this.container.querySelector('.sp-slide')!;
    this.counterEl = this.container.querySelector('.sp-counter')!;
    this.progressEl = this.container.querySelector('.sp-progress-bar')!;
    this.sectionTitleEl = this.container.querySelector('.sp-section-title')!;

    this.wireEvents();
    this.goTo(0);
  }

  dispose(): void {
    document.removeEventListener('keydown', this.boundKeydown);
    this.container.classList.remove('slide-presenter');
  }

  private wireEvents(): void {
    // Button navigation
    this.container.querySelector('.sp-prev')!.addEventListener('click', () => this.prev());
    this.container.querySelector('.sp-next')!.addEventListener('click', () => this.next());
    this.container.querySelector('.sp-exit')!.addEventListener('click', () => this.options.onExit());

    // Keyboard
    document.addEventListener('keydown', this.boundKeydown);

    // Touch — swipe left/right on the viewport
    const viewport = this.container.querySelector('.sp-slide-viewport')!;
    viewport.addEventListener('touchstart', (e) => {
      const t = (e as TouchEvent).touches[0];
      this.touchStartX = t.clientX;
      this.touchStartY = t.clientY;
    }, { passive: true });

    viewport.addEventListener('touchend', (e) => {
      const t = (e as TouchEvent).changedTouches[0];
      const dx = t.clientX - this.touchStartX;
      const dy = t.clientY - this.touchStartY;
      if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) {
        if (dx < 0) this.next();
        else this.prev();
      }
    }, { passive: true });

    // Tap zones — left 40% = prev, right 40% = next, center 20% = nothing
    viewport.addEventListener('click', (e) => {
      const rect = viewport.getBoundingClientRect();
      const x = (e as MouseEvent).clientX - rect.left;
      const pct = x / rect.width;
      if (pct < 0.4) this.prev();
      else if (pct > 0.6) this.next();
    });
  }

  private onKeydown(e: KeyboardEvent): void {
    if (e.key === 'ArrowRight' || e.key === ' ') {
      e.preventDefault();
      this.next();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this.prev();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      this.options.onExit();
    }
  }

  private next(): void {
    if (this.current < this.slides.length - 1) this.goTo(this.current + 1);
  }

  private prev(): void {
    if (this.current > 0) this.goTo(this.current - 1);
  }

  private goTo(index: number): void {
    this.current = index;
    const slide = this.slides[index];

    // Update content
    const subtitleHtml = slide.subtitle
      ? `<span class="sp-slide-subtitle">${slide.subtitle}</span>`
      : '';
    this.slideEl.innerHTML = `${subtitleHtml}<div class="sp-slide-body">${slide.html}</div>`;

    // Update header
    this.sectionTitleEl.textContent = slide.sectionTitle;
    this.counterEl.textContent = `${index + 1} / ${this.slides.length}`;

    // Update progress
    const pct = ((index + 1) / this.slides.length) * 100;
    this.progressEl.style.width = `${pct}%`;

    // Nav button states
    this.container.querySelector('.sp-prev')!.toggleAttribute('disabled', index === 0);
    this.container.querySelector('.sp-next')!.toggleAttribute('disabled', index === this.slides.length - 1);
  }
}

/**
 * Splits a long text into slide-sized chunks.
 * Each paragraph (double newline) becomes a slide.
 * Paragraphs over `maxWords` are split at the nearest sentence boundary.
 */
export function textToSlides(text: string, sectionTitle: string, maxWords = 120): Slide[] {
  const paragraphs = text.split(/\n\n+/).map((p) => p.trim()).filter(Boolean);
  const slides: Slide[] = [];

  for (const para of paragraphs) {
    const words = para.split(/\s+/);
    if (words.length <= maxWords) {
      slides.push({ sectionTitle, html: `<p>${escHtml(para)}</p>` });
    } else {
      // Split at sentence boundary nearest to halfway
      const sentences = para.split(/(?<=[.!?])\s+/);
      let chunk = '';
      let wordCount = 0;
      for (const sentence of sentences) {
        const sWords = sentence.split(/\s+/).length;
        if (wordCount > 0 && wordCount + sWords > maxWords) {
          slides.push({ sectionTitle, html: `<p>${escHtml(chunk.trim())}</p>` });
          chunk = '';
          wordCount = 0;
        }
        chunk += (chunk ? ' ' : '') + sentence;
        wordCount += sWords;
      }
      if (chunk.trim()) {
        slides.push({ sectionTitle, html: `<p>${escHtml(chunk.trim())}</p>` });
      }
    }
  }

  return slides;
}

function escHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
